"use client";

import { Copy, Download } from "iconoir-react";
import * as React from "react";
import { toast } from "sonner";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";

const BRAND_ASSETS_ZIP_PATH =
  "/assets-next/brand-guidelines/wayflyer-brand-assets.zip";
const LOGOTYPE_SVG_PATH = "/assets-next/wayflyer-logo-v2.svg";
const BRAND_SYMBOL_SVG_PATH = "/assets-next/brand-guidelines/symbol-black.svg";

type BrandAssetsMenuItemProps = {
  label: string;
  onSelect: () => void;
  icon: React.ReactNode;
};

function BrandAssetsMenuItem({
  label,
  onSelect,
  icon,
}: BrandAssetsMenuItemProps) {
  return (
    <ContextMenuItem onClick={onSelect}>
      {icon}
      <span className="text-grey-950 text-sm leading-[1.5]">{label}</span>
    </ContextMenuItem>
  );
}

function MenuIconBox({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "rounded-ds-m border-grey-100 bg-grey-50 group-data-highlighted/context-menu-item:border-pulse-700 group-data-highlighted/context-menu-item:bg-pulse-700 relative flex size-6 shrink-0 items-center justify-center overflow-hidden border transition-[background-color,border-color] duration-150",
        className
      )}
    >
      {children}
    </span>
  );
}

function BrandSymbolIcon() {
  return (
    <svg
      viewBox="0 0 87 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="text-grey-950 group-data-highlighted/context-menu-item:text-grey-0 h-[9px] w-3 transition-colors duration-150"
    >
      <path
        fill="currentColor"
        d="M58.4961 0V12.4571H69.8862V12.8L61.6441 43.8857H61.3579L50.3684 9.88572H27.073V22.4H30.2783V22.8L23.8105 43.8857H23.4099L16.5987 18.2857H0V30.8H6.98289V31.1429L16.3125 64H29.248L42.0691 28.5714H42.3553L55.8059 64H68.6842L87 0H58.4961Z"
      />
    </svg>
  );
}

async function copySvgToClipboard(path: string, label: string) {
  try {
    const response = await fetch(path);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}`);
    }

    const svg = await response.text();
    await navigator.clipboard.writeText(svg);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`);
  }
}

function downloadBrandAssets() {
  const link = document.createElement("a");
  link.href = BRAND_ASSETS_ZIP_PATH;
  link.download = "wayflyer-brand-assets.zip";
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  toast.success("Brand assets download started");
}

export function BrandAssetsContextMenu({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger className="inline-flex">
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-[229px]">
        <BrandAssetsMenuItem
          label="Download brand assets"
          onSelect={downloadBrandAssets}
          icon={
            <MenuIconBox>
              <Download
                className="text-grey-950 group-data-highlighted/context-menu-item:text-grey-0 size-4 transition-colors duration-150"
                strokeWidth={1.75}
                aria-hidden
              />
            </MenuIconBox>
          }
        />
        <BrandAssetsMenuItem
          label="Copy logotype as SVG"
          onSelect={() => copySvgToClipboard(LOGOTYPE_SVG_PATH, "Logotype")}
          icon={
            <MenuIconBox>
              <Copy
                className="text-grey-950 group-data-highlighted/context-menu-item:text-grey-0 size-4 transition-colors duration-150"
                strokeWidth={1.75}
                aria-hidden
              />
            </MenuIconBox>
          }
        />
        <BrandAssetsMenuItem
          label="Copy brand symbol as SVG"
          onSelect={() =>
            copySvgToClipboard(BRAND_SYMBOL_SVG_PATH, "Brand symbol")
          }
          icon={
            <MenuIconBox>
              <BrandSymbolIcon />
            </MenuIconBox>
          }
        />
      </ContextMenuContent>
    </ContextMenu>
  );
}
